<script lang="ts">
  import { onMount } from "svelte"
  import InfiniteScroll from "$app/components/InfiniteScroll.svelte"
  import debounce from "lodash/debounce"
  import { xhr, toast } from "$app/utils"
  import { Search, Info, X } from "lucide-svelte"
  import Honeybadger from "$app/utils/honeybadger"
  import { appStore } from "$app/store.svelte"

  type Props = {
    statement: StatementType
    onupdate?: (data: StatementType) => void
    oncancel?: () => void
  }

  let props = $props()
  let { statement, onupdate = () => {}, oncancel = () => {} }: Props = props

  let symbols: any[] = $state([])
  let selectedSymbol: any = $state(null)
  let page = 1
  let searchQuery = $state("")
  let loading: boolean = $state(false)
  let saving: boolean = $state(false)
  let tab: string = $state("global")
  let prevTab: string = $state(tab)
  let hasMore: boolean = $state(true)

  function getUrl(): string {
    if (tab === "global" && searchQuery === "") {
      return `/plan_symbols.json?page=${page}`
    } else if (tab === "global" && searchQuery !== "") {
      return `/plan_symbols/search.json?search=${searchQuery}&page=${page}`
    } else if (tab === "plan" && searchQuery === "") {
      return `/plans/${appStore.plan.id}/symbols.json?page=${page}`
    } else {
      return `/plans/${appStore.plan.id}/symbols.json?search=${searchQuery}&page=${page}`
    }
  }

  async function loadSymbols(): Promise<void> {
    loading = true
    try {
      const resp = await xhr.get(getUrl())
      if (resp.data.length > 0) {
        symbols = [...symbols, ...resp.data]
      } else {
        hasMore = false
      }
    } catch (e) {
      if (typeof Honeybadger.notify !== "undefined") {
        Honeybadger.notify(e)
      }
      toast("There was an error loading the symbols", "error")
    } finally {
      loading = false
    }
  }

  $effect(() => {
    if (prevTab !== tab) {
      prevTab = tab
      page = 1
      hasMore = true
      symbols = []
      setTimeout(() => {
        loadSymbols()
      }, 100)
    }
  })

  const handleInput = debounce((e) => {
    page = 1
    symbols = []
    loadSymbols()
  }, 300)

  async function updateSymbol(): Promise<void> {
    saving = true

    let url = `/plans/${appStore.plan.id}/statements/${statement.id}.json?channel=${appStore.channel_id}`

    try {
      const resp = await xhr.put<StatementType>(url, {
        statement: {
          symbol_id: selectedSymbol.id
        }
      })
      onupdate(resp.data)
    } catch (e) {
      if (typeof Honeybadger.notify !== "undefined") {
        Honeybadger.notify(e)
      }
      toast(`There was an error updating`, "error")
    } finally {
      saving = false
    }
  }

  onMount(() => {
    loadSymbols()
  })
</script>

<div class="flex">
  <h3 class="font-herme inline text-2xl font-medium text-gray-900 -mt-8 py-2">
    Choose Symbol
  </h3>
</div>
<div class="w-xl border border-gray-300 rounded shadow-sm">
  <div>
    <div class="mt-2 px-4 flex justify-between border-b border-gray-200">
      <nav class="flex tabs">
        <button
          class="tab"
          class:active={tab === "global"}
          onclick={() => (tab = "global")}>Global</button
        >
        <button
          class="tab"
          class:active={tab === "plan"}
          onclick={() => (tab = "plan")}>Plan</button
        >
      </nav>

      <div class="flex">
        <div class="relative mt-2 grid grid-cols-1 mb-2">
          <input
            type="text"
            bind:value={searchQuery}
            oninput={handleInput}
            placeholder="Filter by keyword"
            class="col-start-1 row-start-1 block w-full rounded-md bg-white py-1.5 pr-10 pl-3 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:pr-9 sm:text-sm/6"
          />
          <div class="absolute top-2 right-1">
            {#if searchQuery === ""}
              <Search
                class="pointer-events-none col-start-1 row-start-1 mr-3 size-5 self-center justify-self-end text-gray-400 sm:size-4"
              />
            {:else}
              <button
                onclick={() => {
                  searchQuery = ""
                  handleInput()
                }}
                class="cursor-pointer"
                title="Clear"
              >
                <X class="size-5 text-gray-500" />
              </button>
            {/if}
          </div>
        </div>
      </div>
    </div>

    <div
      class="overflow-y-scroll mt-4 p-4 flex flex-wrap"
      style="height:400px"
      id="infinite-scroll"
    >
      {#each symbols as symbol, idx (idx)}
        <!-- svelte-ignore a11y_click_events_have_key_events -->
        <!-- svelte-ignore a11y_no_static_element_interactions -->
        <div
          class="w-1/2 sm:w-1/3 lg:w-1/4 p-2 {symbol.id === selectedSymbol?.id
            ? 'bg-blue-200 shadow border border-blue-300'
            : 'border-gray-500 hover:bg-blue-100 hover:border hover:shadow'}"
          onclick={() => (selectedSymbol = symbol)}
          ondblclick={() => updateSymbol()}
          data-name="symbol-elem"
        >
          <img
            src={symbol.image_url}
            class="object-scale-down w-32 h-full"
            alt={symbol.name}
          />
        </div>
      {:else}
        <div>
          <div
            class="flex items-center text-lg py-4 px-12 border rounded-lg shadow"
          >
            <Info class="text-gray-700 mr-2" />
            {#if searchQuery.length > 0}
              No Results found for your search
            {:else}
              No Symbols Found
            {/if}
          </div>
        </div>
      {/each}
      <InfiniteScroll
        {hasMore}
        threshold={100}
        onloadmore={() => {
          page++
          loadSymbols()
        }}
      />
    </div>

    <div class="mt-4 flex justify-end border-t border-gray-200 p-2">
      <button type="button" class="btn outline mr-2" onclick={() => oncancel()}>
        Cancel
      </button>
      <button
        type="submit"
        class="btn primary"
        disabled={!selectedSymbol}
        onclick={() => updateSymbol()}
      >
        Choose Symbol
      </button>
    </div>
  </div>
</div>

<style>
  .tabs .tab {
    padding: 0.5rem 1rem;
  }
</style>
